/* eslint-disable react/prop-types */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormHelperText,
  Typography,
  Sheet,
  Snackbar,
} from '@mui/joy';
import Grid from '@mui/joy/Grid';
import Link from '@mui/joy/Link';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import Breadcrumbs from '@mui/joy/Breadcrumbs';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CustomTextField from '../../../main/widgets/CustomTextField';
import NativeCheckbox from '../../../main/components/NativeCheckbox';
import PermissionGate from '../../../main/components/PermissionGate';
import { PERMISSIONS } from '../../../main/context/AuthContext';
import { usePermissions } from '../../../main/hooks/usePermissions';
import {
  createPricingPlan,
  getPricingPlan,
  updatePricingPlan,
} from '../../../main/API/pricingPlan';
import { masterApiErrorMessage } from '../../../main/utils/masterApiError';
import {
  buildPricingPlanPayload,
  emptyPricingPlanForm,
  pricingPlanApiValidationFeedback,
  pricingPlanFromApi,
  validatePricingPlanClient,
} from './pricingPlanFormPayload';
import PricingPlanBandsTable from './PricingPlanBandsTable';

const FIELD_SCROLL_ORDER = [
  'code',
  'name',
  'name_ar',
  'description',
  'description_ar',
  'show_in_menu',
  'menu_label_en',
  'menu_label_ar',
  'menu_order',
  'active',
];

function scrollToFirstFieldError(errors, banner) {
  const errMap = errors && typeof errors === 'object' ? errors : {};
  const tryScroll = (el) => {
    if (!el) return false;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const focusable = el.querySelector(
      'input:not([type="hidden"]), textarea, select, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable && typeof focusable.focus === 'function') {
      try {
        focusable.focus({ preventScroll: true });
      } catch (_e) {
        focusable.focus();
      }
    }
    return true;
  };

  for (let i = 0; i < FIELD_SCROLL_ORDER.length; i++) {
    const key = FIELD_SCROLL_ORDER[i];
    if (errMap[key]) {
      const el = document.getElementById(`pricing-plan-field-${key}`);
      if (tryScroll(el)) return;
    }
  }
  if (banner) {
    const el = document.getElementById('pricing-plan-save-error');
    if (tryScroll(el)) return;
  }
}

/**
 * Shared create/edit form for PricingPlan.
 * When `mode === 'edit'`, parent may render bands below via `children` or a sibling section.
 */
export default function PricingPlanForm({
  mode,
  planId,
  onSaved,
  hideBreadcrumbs = false,
  embed = false,
}) {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyPricingPlanForm);
  const [fetching, setFetching] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [success, setSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const pendingScrollToErrorsRef = useRef(false);

  const canSubmit =
    mode === 'create'
      ? hasPermission(PERMISSIONS.PRICING_PLANS_CREATE)
      : hasPermission(PERMISSIONS.PRICING_PLANS_UPDATE);

  useEffect(() => {
    if (mode !== 'edit' || !planId) {
      setFetching(false);
      return;
    }
    if (!hasPermission(PERMISSIONS.PRICING_PLANS_VIEW)) {
      setLoadError(t('permissions.noPermission', { defaultValue: 'Permission denied.' }));
      setFetching(false);
      return;
    }
    let cancelled = false;
    setFetching(true);
    getPricingPlan(planId)
      .then((res) => {
        if (cancelled) return;
        setForm(pricingPlanFromApi(res.data));
        setLoadError('');
      })
      .catch((e) => {
        if (!cancelled) setLoadError(masterApiErrorMessage(e, t));
      })
      .finally(() => {
        if (!cancelled) setFetching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, planId, hasPermission, t]);

  useLayoutEffect(() => {
    if (!pendingScrollToErrorsRef.current) return;
    pendingScrollToErrorsRef.current = false;
    scrollToFirstFieldError(fieldErrors, saveError);
  }, [fieldErrors, saveError]);

  const patch = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError('');
    const v = validatePricingPlanClient(form, t, { isCreate: mode === 'create' });
    setFieldErrors(v);
    if (Object.keys(v).length) {
      pendingScrollToErrorsRef.current = true;
      return;
    }
    if (!canSubmit) return;
    const payload = buildPricingPlanPayload(form);
    setSaving(true);
    try {
      if (mode === 'create') {
        const res = await createPricingPlan(payload);
        const id = res?.data?.id;
        if (typeof onSaved === 'function') {
          onSaved(res.data);
        } else if (id != null) {
          navigate(`/PricingPlans/${id}`, { state: { pricingPlanJustCreated: true } });
        } else {
          navigate('/PricingPlans', { state: { pricingPlanJustCreated: true } });
        }
      } else {
        await updatePricingPlan(planId, payload);
        setSuccess(true);
        if (typeof onSaved === 'function') onSaved(payload);
      }
    } catch (err) {
      const { fieldErrors: apiFieldErrors, saveError: apiBanner } =
        pricingPlanApiValidationFeedback(err, t);
      setFieldErrors(apiFieldErrors);
      setSaveError(apiBanner);
      if (Object.keys(apiFieldErrors).length > 0 || apiBanner) {
        pendingScrollToErrorsRef.current = true;
      }
    } finally {
      setSaving(false);
    }
  };

  const title =
    mode === 'create' ? t('pricingPlans.formAddTitle') : t('pricingPlans.formEditTitle');

  return (
    <Box sx={embed ? undefined : { m: 3 }}>
      {!hideBreadcrumbs && (
        <Box mb={2}>
          <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
            <Link color="neutral" href="/PricingPlans">
              {t('nav.pricingPlans')}
            </Link>
            <Typography>{title}</Typography>
          </Breadcrumbs>
          <Typography level="h1" textAlign="start">
            {title}
          </Typography>
        </Box>
      )}

      {loadError ? (
        <Typography color="danger" sx={{ mb: 2 }}>
          {loadError}
        </Typography>
      ) : null}

      {fetching ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 280,
            gap: 2,
          }}
        >
          <CircularProgress size="lg" />
          <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
            {t('actions.loading')}
          </Typography>
        </Box>
      ) : (
        <Sheet
          variant="outlined"
          sx={{ p: 3, borderRadius: 'md', bgcolor: 'background.surface' }}
          component="form"
          onSubmit={handleSubmit}
        >
          {saveError ? (
            <Typography id="pricing-plan-save-error" color="danger" sx={{ mb: 2 }}>
              {saveError}
            </Typography>
          ) : null}

          <Typography level="title-lg" sx={{ mb: 2 }}>
            {t('pricingPlans.sectionIdentity')}
          </Typography>
          <Grid container spacing={2}>
            <Grid xs={12} md={4} id="pricing-plan-field-code">
              <CustomTextField
                label={t('pricingPlans.fieldCode')}
                hintTooltip={t('pricingPlans.hintCode')}
                value={form.code}
                onChange={(e) => patch('code', e.target.value)}
                required
                disabled={mode === 'edit' || saving}
                fullWidth
              />
              {fieldErrors.code ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{fieldErrors.code}</FormHelperText>
              ) : null}
            </Grid>
            <Grid xs={12} md={4} id="pricing-plan-field-name">
              <CustomTextField
                label={t('pricingPlans.fieldName')}
                value={form.name}
                onChange={(e) => patch('name', e.target.value)}
                required
                disabled={saving}
                fullWidth
              />
              {fieldErrors.name ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{fieldErrors.name}</FormHelperText>
              ) : null}
            </Grid>
            <Grid xs={12} md={4} id="pricing-plan-field-name_ar">
              <CustomTextField
                label={t('pricingPlans.fieldNameAr')}
                value={form.name_ar}
                onChange={(e) => patch('name_ar', e.target.value)}
                disabled={saving}
                fullWidth
              />
              {fieldErrors.name_ar ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{fieldErrors.name_ar}</FormHelperText>
              ) : null}
            </Grid>
            <Grid xs={12} md={6} id="pricing-plan-field-description">
              <CustomTextField
                label={t('pricingPlans.fieldDescription')}
                value={form.description}
                onChange={(e) => patch('description', e.target.value)}
                disabled={saving}
                fullWidth
              />
            </Grid>
            <Grid xs={12} md={6} id="pricing-plan-field-description_ar">
              <CustomTextField
                label={t('pricingPlans.fieldDescriptionAr')}
                value={form.description_ar}
                onChange={(e) => patch('description_ar', e.target.value)}
                disabled={saving}
                fullWidth
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography level="title-lg" sx={{ mb: 1 }}>
            {t('pricingPlans.sectionMenu')}
          </Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.600', mb: 2 }}>
            {t('pricingPlans.sectionMenuHint')}
          </Typography>
          <Grid container spacing={2}>
            <Grid xs={12} sm={6} id="pricing-plan-field-show_in_menu">
              <NativeCheckbox
                checked={form.show_in_menu}
                onChange={(e) => patch('show_in_menu', e.target.checked)}
                label={t('pricingPlans.fieldShowInMenu')}
                disabled={saving}
              />
            </Grid>
            <Grid xs={12} sm={6} id="pricing-plan-field-active">
              <NativeCheckbox
                checked={form.active}
                onChange={(e) => patch('active', e.target.checked)}
                label={t('pricingPlans.fieldActive')}
                disabled={saving}
              />
            </Grid>
            <Grid xs={12} md={4} id="pricing-plan-field-menu_label_en">
              <CustomTextField
                label={t('pricingPlans.fieldMenuLabelEn')}
                value={form.menu_label_en}
                onChange={(e) => patch('menu_label_en', e.target.value)}
                disabled={saving}
                fullWidth
              />
            </Grid>
            <Grid xs={12} md={4} id="pricing-plan-field-menu_label_ar">
              <CustomTextField
                label={t('pricingPlans.fieldMenuLabelAr')}
                hintTooltip={t('pricingPlans.hintMenuLabelAr')}
                value={form.menu_label_ar}
                onChange={(e) => patch('menu_label_ar', e.target.value)}
                disabled={saving}
                fullWidth
              />
            </Grid>
            <Grid xs={12} md={4} id="pricing-plan-field-menu_order">
              <CustomTextField
                label={t('pricingPlans.fieldMenuOrder')}
                type="number"
                value={form.menu_order}
                onChange={(e) => patch('menu_order', e.target.value)}
                disabled={saving}
                fullWidth
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 1, mt: 3, flexWrap: 'wrap' }}>
            <PermissionGate
              permission={
                mode === 'create'
                  ? PERMISSIONS.PRICING_PLANS_CREATE
                  : PERMISSIONS.PRICING_PLANS_UPDATE
              }
            >
              <Button type="submit" loading={saving} disabled={!canSubmit || saving}>
                {mode === 'create' ? t('actions.submit') : t('actions.save')}
              </Button>
            </PermissionGate>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => navigate('/PricingPlans')}
              disabled={saving}
            >
              {t('pricingPlans.backToList')}
            </Button>
          </Box>
        </Sheet>
      )}

      {mode === 'edit' && planId && !fetching ? (
        <PricingPlanBandsTable planId={planId} />
      ) : null}

      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        color="success"
        variant="soft"
      >
        {t('pricingPlans.snackbarUpdated')}
      </Snackbar>
    </Box>
  );
}
